import { useTranslation } from "react-i18next";
import { FadeUp, MaskReveal, Stagger, StaggerItem } from "@/components/Motion";
import useSEO, { canonicalFor } from "@/lib/useSEO";

const MOOD_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3e477c2a-f98b-44c6-92b8-bae42dd1c756/images/f5d1c19c073c283d5186002cbe4b6da0df54ddc76efa652eb9e0b79ec571dd2b.png";
const FIGURE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3e477c2a-f98b-44c6-92b8-bae42dd1c756/images/99b1a542992bdd4e6d6ac6dfb6bbb72c1daa2b015a6bb9893ced7c58a561ed22.png";
const TEXTURE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3e477c2a-f98b-44c6-92b8-bae42dd1c756/images/ba882160173d5126b25b664719240c651a32bbf5988a4e703e0e9dc729c0e269.png";

const REACH_COUNTRIES = [
  "Cyprus", "Greece", "Malta", "Luxembourg", "Ireland", "Netherlands",
  "Germany", "France", "Italy", "Spain", "Portugal", "Belgium",
  "Austria", "Estonia", "Lithuania", "Czechia", "Poland", "Bulgaria",
];

export default function About() {
  useSEO({
    title: "The firm — Corpora Consultants",
    description: "Corpora Consultants is a Cyprus-based corporate law practice advising international operators on cross-border legal, regulatory and tax architecture.",
    canonical: canonicalFor("/about"),
  });

  const { t } = useTranslation();
  const stats = t("about.stats", { returnObjects: true });

  return (
    <div data-testid="about-page">
      <section className="relative h-[80vh] min-h-[600px] overflow-hidden grain">
        <img
          src={MOOD_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "brightness(0.55)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,11,13,0.7) 0%, rgba(10,11,13,0.4) 30%, rgba(10,11,13,0.95) 100%)",
          }}
        />
        <div className="relative z-10 h-full max-w-[1480px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-20">
          <div className="overline mb-6">{t("about.eyebrow")}</div>
          <h1 className="display text-[var(--bone)]" style={{ fontSize: "clamp(64px, 10vw, 160px)" }}>
            <div>
              <MaskReveal>{t("about.heroLine1")}</MaskReveal>
            </div>
            <div className="display-italic" style={{ color: "var(--pewter)" }}>
              <MaskReveal delay={0.2}>{t("about.heroLine2")}</MaskReveal>
            </div>
          </h1>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <FadeUp>
              <div className="overline mb-6">{t("about.introEyebrow")}</div>
              <h2 className="display text-4xl md:text-5xl text-[var(--bone)] leading-tight">
                {t("about.introTitleA")}
                <br />
                {t("about.introTitleB")}
                <br />
                <span className="display-italic" style={{ color: "var(--pewter)" }}>
                  {t("about.introTitleC")}
                </span>
              </h2>
            </FadeUp>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-6 text-[var(--bone-2)] text-lg leading-[1.8] font-light">
            <FadeUp delay={0.1}><p>{t("about.introP1")}</p></FadeUp>
            <FadeUp delay={0.2}><p>{t("about.introP2")}</p></FadeUp>
            <FadeUp delay={0.3}><p>{t("about.introP3")}</p></FadeUp>
          </div>
        </div>
      </section>

      <section className="section-pad relative overflow-hidden" style={{ background: "var(--ink-2)" }}>
        <img src={TEXTURE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.07]" />
        <div className="relative z-10 max-w-[1480px] mx-auto px-6 md:px-10">
          <FadeUp>
            <div className="overline mb-8">{t("about.statsEyebrow")}</div>
          </FadeUp>
          <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--line)]">
            {stats.map((s, i) => (
              <StaggerItem key={i} className="bg-[var(--ink-2)] p-10 min-h-[200px] flex flex-col justify-between">
                <div className="display text-[var(--bone)]" style={{ fontSize: "clamp(56px, 7vw, 96px)" }}>
                  {s.n}
                </div>
                <div className="overline mt-6">{s.label}</div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <FadeUp>
              <div className="aspect-[4/5] overflow-hidden">
                <img src={FIGURE_IMG} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(0.2) brightness(0.85)" }} />
              </div>
            </FadeUp>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <FadeUp delay={0.1}>
              <div className="overline mb-6">{t("about.philosophyEyebrow")}</div>
              <h2 className="display text-4xl md:text-5xl text-[var(--bone)] leading-tight">
                {t("about.philosophyTitleA")}
                <br />
                <span className="display-italic" style={{ color: "var(--pewter)" }}>
                  {t("about.philosophyTitleB")}
                </span>
              </h2>
              <div className="mt-8 space-y-5 text-[var(--bone-2)] leading-[1.8] font-light">
                <p>{t("about.philosophyP1")}</p>
                <p>{t("about.philosophyP2")}</p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="section-pad border-t border-[var(--line)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <FadeUp>
                <div className="overline mb-6">{t("about.reachEyebrow")}</div>
                <h2 className="display text-5xl md:text-6xl text-[var(--bone)]">
                  {t("about.reachTitleA")}
                  <br />
                  {t("about.reachTitleB")}{" "}
                  <span className="display-italic" style={{ color: "var(--pewter)" }}>
                    {t("about.reachTitleC")}
                  </span>
                </h2>
              </FadeUp>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <FadeUp delay={0.15}>
                <p className="text-lg md:text-xl text-[var(--bone-2)] leading-relaxed font-light">
                  {t("about.reachLead")}
                </p>
              </FadeUp>
              <Stagger className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6 font-mono text-[12px] uppercase tracking-[0.18em] text-[var(--bone-2)]">
                {REACH_COUNTRIES.map((c) => (
                  <StaggerItem key={c} className="border-t border-[var(--line)] pt-3">
                    {c}
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
